import type { QueryState } from '@tanstack/react-query'

import type { PendingEvent } from '@/lib/db'

import type { WorkSessionsMeResponse } from './use-work-sessions'

/**
 * Najstarszy stan z serwera, na podstawie którego wolno jeszcze kliknąć.
 * `/me` odświeża się co 30 s, więc 90 s to trzy chybione odświeżenia — dłużej
 * ekran mógłby pokazywać „pracujesz" przy sesji, którą serwer już zamknął.
 */
export const SERVER_STATE_MAX_AGE_MS = 90_000

export type ClockAvailability =
  /** Wolno kliknąć: świeży stan z serwera i nic nie czeka w starej kolejce. */
  | { kind: 'ok' }
  /** Zaległy zapis poprzedniej wersji — nowe kliknięcie nie może go wyprzedzić. */
  | { kind: 'legacy'; head: PendingEvent }
  /** Brak sieci albo ostatnie pytanie o stan nie dostało odpowiedzi. */
  | { kind: 'offline' }
  /** Pierwsze ładowanie albo odświeżanie po starych/unieważnionych danych. */
  | { kind: 'checking' }

export interface ClockAvailabilityInput {
  /** `undefined` — IndexedDB jeszcze nie odpowiedziała. */
  legacy: PendingEvent[] | undefined
  online: boolean
  server: QueryState<WorkSessionsMeResponse> | undefined
  now: number
}

/** Dane z serwera, którym wolno wierzyć w tej chwili. */
export function isServerStateFresh(
  server: QueryState<WorkSessionsMeResponse> | undefined,
  now: number,
): boolean {
  if (!server || server.status !== 'success') return false
  // Unieważnione = ktoś wie, że serwer ma już inny stan (zapis, 409, błąd sieci).
  if (server.isInvalidated) return false
  return now - server.dataUpdatedAt < SERVER_STATE_MAX_AGE_MS
}

/**
 * Jedno źródło decyzji „czy przycisk start/koniec jest aktywny". Liczone przy
 * renderze i ponownie w handlerze kliknięcia — zamrożona w tle aplikacja nie
 * odpala timerów, więc sam render potrafi pokazywać stan sprzed godzin.
 */
export function clockAvailability({
  legacy,
  online,
  server,
  now,
}: ClockAvailabilityInput): ClockAvailability {
  // Kolejność ma znaczenie: zaległy zapis wygrywa nawet ze świeżym `/me`,
  // bo kliknięcie przed jego wysłaniem odwróciłoby kolejność start → koniec.
  if (legacy === undefined) return { kind: 'checking' }
  const head = legacy[0]
  if (head) return { kind: 'legacy', head }
  // navigator.onLine === false jest pewne; true niczego nie dowodzi, stąd reszta.
  if (!online) return { kind: 'offline' }
  if (isServerStateFresh(server, now)) return { kind: 'ok' }
  // Ostatnie pytanie padło — komunikat zostaje także w trakcie ponowienia,
  // żeby co 30 s nie przeskakiwał między „brak połączenia" a „sprawdzam".
  // Zapytanie bez danych wraca na czas ponowienia do statusu 'pending'
  // (`errorUpdateCount` to przeżywa) — bez drugiego warunku znikał wtedy
  // razem z komunikatem przycisk „Sprawdź ponownie", a z nim fokus klawiatury.
  if (
    server?.status === 'error' ||
    (server?.data === undefined && (server?.errorUpdateCount ?? 0) > 0)
  ) {
    return { kind: 'offline' }
  }
  return { kind: 'checking' }
}
